import { IMediaPlaybackStateInfo } from '@omni/kit/components/KitPlayer/playbackTypes';
import { IMediaPlays } from '@omni/kit/services/MediaPersonalizationService';
import { getMediaPlaybackStateInfo } from '@omni/kit/utilities/NativeHelpers';
import { trackMediaProgressCache } from '../../../../packages/TrackMediaProgress';
import FetchMediaPlayByUserAndMediaItem from '../../../../packages/TrackMediaProgress/FetchMediaPlayByUserAndMediaItem';
import PatchMediaPlayResource from '../../../../packages/TrackMediaProgress/PatchMediaPlayResource';
import { ICachedProgress } from '../../../../packages/TrackMediaProgress/types';

const debug = require('debug')(
  'tca:blocks:MediaBannerBlock:GetLatestMediaPlay'
);

/**
 * Get the latest media play either by
 * - patching media progress to media play resource if the media item is currently playing in the background
 * - fetching media play resource
 *
 * @param userId {string}
 * @param mediaItemId {string}
 * @param appKey {string}
 *
 * @returns IMediaPlays | null
 */
export default async function getLatestMediaPlay({
  userId,
  mediaItemId,
  appKey,
  signal,
}: {
  userId: string;
  mediaItemId: string;
  appKey: string;
  signal?: AbortSignal;
}): Promise<IMediaPlays | null> {
  let currentTrackedMediaProgress: ICachedProgress | undefined;
  let currentMediaPlaybackStateInfo: IMediaPlaybackStateInfo | undefined;

  try {
    currentMediaPlaybackStateInfo = await getMediaPlaybackStateInfo();
    debug('Get currentMediaPlaybackStateInfo', currentMediaPlaybackStateInfo);

    const jsonString = await trackMediaProgressCache.get(
      currentMediaPlaybackStateInfo?.savedStateKey || ''
    );
    currentTrackedMediaProgress = JSON.parse(jsonString || '');
  } catch (e) {
    debug('Failed to retrieve tracked media progress cached ', e);
  }

  /**
   * If the media detail page is the same as the actively playig media item
   * then patch the latest progress from native storage. Otherwise fetch
   * media play from server.
   */
  if (
    currentTrackedMediaProgress &&
    currentTrackedMediaProgress.mediaItemId === mediaItemId
  ) {
    debug('Patch media play resource base on local saved progress');

    const { durationMsec = 0, positionMsec = 0 } =
      currentMediaPlaybackStateInfo || {};
    const { mediaPlayId, mediaItemId, userId } = currentTrackedMediaProgress;

    /** Patch latest progress to media play resource */
    const mediaPlay = await PatchMediaPlayResource({
      appKey,
      duration: durationMsec,
      mediaItemId: mediaItemId,
      mediaPlayId: mediaPlayId,
      position: positionMsec,
      userProfileId: userId,
    });

    /** Return media play if patching succeeds otherwise fallback to fetching media play */
    if (mediaPlay) return mediaPlay;
  }

  /** Fetch media play resource */
  try {
    debug('Fetch media play resource');

    return FetchMediaPlayByUserAndMediaItem({
      appKey,
      userId,
      mediaItemId,
      signal,
    });
  } catch (e) {
    debug('failed to retrieve progress', e);

    return null;
  }
}
